import React, { useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  Platform,
  Alert,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import CachedImage from '@/components/CachedImage';
import { useRouter } from 'expo-router';
import PrimaryButton from '@/components/PrimaryButton';
import SecondaryButton from '@/components/SecondaryButton';
import GoogleButton from '@/components/GoogleButton';
import AuthDivider from '@/components/AuthDivider';
import LanguageButton from '@/components/LanguageButton';
import { useLanguage } from '@/contexts/LanguageContext';
import { googleAuthService } from '@/services/googleAuthService';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

export default function WelcomeScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [googleLoading, setGoogleLoading] = React.useState(false);

  useEffect(() => {
    console.log('🎨 Welcome screen rendered on platform:', Platform.OS);
    console.log('🔤 Font family regular:', styles.titleRegular.fontFamily);
    console.log('🔤 Font family italic:', styles.titleItalic.fontFamily);
    console.log('🔤 Testing Sentient font rendering on Android');
  }, []);

  const triggerHaptic = () => {
    import('expo-haptics')
      .then((Haptics) =>
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light)
      )
      .catch(() => {});
  };

  const handleStartTrail = () => {
    triggerHaptic();
    // Fire-and-forget: marks the entry into the onboarding funnel.
    import('@/utils/clarityEvents')
      .then(({ logClarityEvent }) =>
        logClarityEvent('onboarding_started', { source: 'welcome' })
      )
      .catch(() => {});
    router.push('/onboarding-welcome');
  };

  const handleLogin = () => {
    triggerHaptic();
    router.push('/login');
  };

  const handleGoogleSignIn = async () => {
    if (googleLoading) return;
    triggerHaptic();
    setGoogleLoading(true);
    import('@/utils/clarityEvents')
      .then(({ logClarityEvent }) =>
        logClarityEvent('google_signin_tapped', { source: 'welcome' })
      )
      .catch(() => {});
    try {
      // Clear any stale "onboarding in progress" flag from an abandoned email
      // signup BEFORE authenticating. Otherwise AuthGuard, which checks that
      // flag first, could jump this Google user straight to onboarding-success.
      try {
        const { OnboardingStateService } = await import('@/services/onboardingStateService');
        await OnboardingStateService.clearOnboardingState();
      } catch {}

      await googleAuthService.signIn();
      // On success, signInWithIdToken sets the Supabase session and fires
      // SIGNED_IN. AuthGuard is the SINGLE routing authority for the Google path:
      // it sends new users to onboarding-welcome and returning users home. We do
      // NOT navigate here, to avoid a redirect race with AuthGuard.
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message !== 'CANCELLED') {
        console.warn('⚠️ [Welcome] Google sign-in failed:', message);
        Alert.alert(t('welcome.google.errorTitle'), t('welcome.google.errorMessage'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      {/* Background Image */}
      <CachedImage
        source={require('../assets/images/welcome-background-new.png')}
        style={styles.backgroundImage}
        resizeMode="cover"
      />

      {/* Beige gradient overlay — solidifies toward the bottom so the title and
          buttons sit on a readable beige base instead of the photo. */}
      <LinearGradient
        colors={['rgba(249,248,243,0)', 'rgba(249,248,243,1)']}
        locations={[0.32, 0.61]}
        style={styles.gradientOverlay}
        pointerEvents="none"
      />

      {/* Language Button - Top Right (Outside SafeAreaView) */}
      <SafeAreaView style={styles.languageButtonSafeArea}>
        <View style={styles.languageButtonContainer}>
          <LanguageButton />
        </View>
      </SafeAreaView>

      <SafeAreaView style={styles.safeArea} edges={['top']}>
        {/* Content Container */}
        <View style={styles.contentContainer}>
          {/* Symbol and Title */}
          <View style={styles.heroSection}>
            <Text style={styles.title}>
              <Text style={styles.titleRegular}>{t('welcome.title.part1')}</Text>
              <Text style={styles.titleItalic}>{t('welcome.title.part2')}</Text>
              <Text style={styles.titleRegular}>{t('welcome.title.part3')}</Text>
            </Text>
          </View>

          {/* Buttons Container */}
          <View style={[styles.buttonContainer, { paddingBottom: Math.max(insets.bottom, 12) }]}>
            <GoogleButton
              title={t('welcome.button.google')}
              onPress={handleGoogleSignIn}
              loading={googleLoading}
            />
            <PrimaryButton
              title={t('welcome.button.start')}
              onPress={handleStartTrail}
              style={styles.roundedButton}
            />
            <AuthDivider label={t('welcome.divider.or')} />
            <SecondaryButton
              title={t('welcome.button.login')}
              onPress={handleLogin}
              style={styles.loginButton}
            />
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: screenWidth,
    height: screenHeight,
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: screenWidth,
    height: screenHeight,
  },
  safeArea: {
    flex: 1,
  },
  languageButtonSafeArea: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1000,
  },
  languageButtonContainer: {
    paddingTop: 24,
    paddingRight: 24,
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    paddingVertical: 24,
  },
  heroSection: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 40,
    gap: 24,
    marginBottom: 32,
  },
  title: {
    color: '#151515',
    textAlign: 'center',
    fontSize: 40,
    lineHeight: 44,
    maxWidth: 320,
  },
  titleRegular: {
    fontFamily: 'InstrumentSerif_400Regular',
  },
  titleItalic: {
    fontFamily: 'InstrumentSerif_400Regular_Italic',
  },
  buttonContainer: {
    paddingHorizontal: 24,
    gap: 12,
  },
  roundedButton: {
    borderRadius: 12,
  },
  loginButton: {
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    borderWidth: 0,
  },
});
